import { Button } from '@accure/ui';

import { Card } from '@/components/ui/card';
import { useCurrentAsset } from '@/hooks/use-current-asset';

import { GetFiltersQuery } from '../../api/queries/get-filters';
import { initialFilters } from '../state/selected-filters';
import { useSystemLogsQueryState } from '../state/use-system-logs-query';

import { FilterCountDisplay } from './filter-count-display';
import {
  DomainFilter,
  ServerLevelFilters,
  SiteFilter,
  SystemLogsDateRangeFilter,
} from './filters';
import { FiltersLoading } from './system-logs-filters-loading';

export type SystemLogsFiltersProps = {
  /** True when viewing logs for a specific asset (site filter hidden), false in portfolio view (site filter shown) */
  isAssetContext: boolean;
};

export function SystemLogsFilters({ isAssetContext }: SystemLogsFiltersProps) {
  const asset = useCurrentAsset();

  // human-readable URL state
  const [filters, setFilters] = useSystemLogsQueryState();
  const assetId = asset.assetId;

  // get filters from query
  const filtersQueryResult = GetFiltersQuery.useQuery({
    assetId: assetId,
    filters: filters,
  });
  const filtersQuery = filtersQueryResult.data?.filters;
  const isLoading = filtersQueryResult.isLoading;
  const isFetching = filtersQueryResult.isFetching;

  return (
    <Card
      className="flex w-full flex-col gap-3 p-4"
      data-testid="system-logs-filters"
    >
      {isLoading ? (
        <FiltersLoading />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <SystemLogsDateRangeFilter
              value={filters.coreFilters.dateRange}
              onChange={(dateRange) =>
                setFilters((s) => ({
                  ...s,
                  coreFilters: { ...s.coreFilters, dateRange },
                }))
              }
            />
            {filtersQuery?.coreFilters.domain ? (
              <DomainFilter
                value={filters.coreFilters.domain}
                onChange={(domain) =>
                  setFilters((s) => ({
                    ...s,
                    coreFilters: { ...s.coreFilters, domain },
                  }))
                }
                options={filtersQuery.coreFilters.domain.values}
                isLoading={isFetching}
              />
            ) : null}
            {!isAssetContext && filtersQuery?.coreFilters.siteId ? (
              <SiteFilter
                value={filters.coreFilters.siteId}
                options={filtersQuery.coreFilters.siteId.values.map((v) => ({
                  id: v.id,
                  label: v.label,
                }))}
                onChange={(siteId) =>
                  setFilters((s) => ({
                    ...s,
                    coreFilters: { ...s.coreFilters, siteId },
                  }))
                }
                isLoading={isFetching}
              />
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              <FilterCountDisplay filters={filters} />
              <Button
                variant="ghost"
                size="sm"
                data-testid="system-logs-clear-all"
                onClick={() => setFilters(initialFilters)}
                disabled={isFetching}
              >
                Clear all
              </Button>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-2">
            <ServerLevelFilters
              domain={filters.coreFilters.domain}
              server={{
                coreFilters: filtersQuery?.coreFilters ?? {
                  dateRange: { from: undefined, to: undefined },
                  domain: undefined,
                  siteId: undefined,
                },
                batteryFilters: filtersQuery?.batteryFilters ?? {},
                pcsFilters: filtersQuery?.pcsFilters ?? {},
              }}
              filters={{ ...filters.batteryFilters, ...filters.pcsFilters }}
              isLoading={isFetching}
              onChange={(updated) => {
                setFilters((s) => ({
                  ...s,
                  batteryFilters:
                    s.coreFilters.domain === 'battery'
                      ? { ...s.batteryFilters, ...updated }
                      : s.batteryFilters,
                  pcsFilters:
                    s.coreFilters.domain === 'pcs'
                      ? { ...s.pcsFilters, ...updated }
                      : s.pcsFilters,
                }));
              }}
            />
          </div>
        </>
      )}
    </Card>
  );
}
